import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PopupNames } from 'core';
import {
  useAllFakturowniaInvoices,
  usePopupStateOperations,
  IFakturowniaInvoiceListRow,
} from 'core/hooks';

import { InvoicesTable } from '../../FakturowniaInvoices/InvoicesTable';
import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';

interface IOrphanInvoicesBlockProps {
  studentId: string;
  onChanged?: () => void;
}

/**
 * «Без договора» — invoices that came from Fakturownia matched to the
 * student by email but never got a canonical Payment link. They're the
 * reconciliation queue; every row carries an inline «Link to Payment»
 * action wired to the Phase 13 popup.
 *
 * Renders nothing when there are no orphans — keeps the dashboard quiet
 * for healthy students.
 */
export const OrphanInvoicesBlock = memo(
  ({ studentId, onChanged }: IOrphanInvoicesBlockProps) => {
    const { t } = useTranslation();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();
    const { data, fetching } = useAllFakturowniaInvoices({
      studentId,
      direction: 'issued',
    });

    const orphans = useMemo(() => data.filter((r) => !r.payment), [data]);

    const onLinkRow = useCallback(
      (row: IFakturowniaInvoiceListRow) => {
        showPopup(PopupNames.linkInvoicePayment, {
          invoiceId: row.id,
          mode: 'link',
          buyerEmail: row.buyerEmail,
          prefillStudentId: studentId,
          onLinked: onChanged,
        });
      },
      [showPopup, studentId, onChanged],
    );

    if (orphans.length === 0) return null;

    return (
      <S.DimWhenFetching fetching={fetching}>
        <S.Card>
          <S.CardTitle>
            {t('pages.studentDetail.invoices.noAgreementSection')}
          </S.CardTitle>
          <S.SubTitle>
            {t('pages.studentDetail.invoices.noAgreementHint')}
          </S.SubTitle>
          <InvoicesTable rows={orphans} onLinkRow={onLinkRow} />
        </S.Card>
      </S.DimWhenFetching>
    );
  },
);
